// feat: define XP rules and level progression

/**
 * XP awarded for various activities
 */
export const XP_RULES = {
  // Content creation
  NOTE_CREATED: 10,
  NOTE_EDITED: 2,
  CANVAS_CREATED: 15,

  // Reading progress
  BOOK_COMPLETED: 50,
  // Awarded on the `net.xdface.library.chapter.read` event emitted by the library repo
  ARTICLE_READ: 5,

  // Course progress
  LESSON_COMPLETED: 20,
  COURSE_COMPLETED: 100,

  // Engagement
  SHARING_UNLOCKED: 25,
} as const;

/**
 * XP required for each level
 * Level 1 starts at 0, each subsequent level requires 1.5x more XP
 */
export const LEVELS: Record<number, number> = {
  1: 0,
  2: 100,
  3: 250,
  4: 450,
  5: 700,
  6: 1000,
  7: 1350,
  8: 1750,
  9: 2200,
  10: 2700,
  11: 3250,
  12: 3850,
  13: 4500,
  14: 5200,
  15: 5950,
  16: 6750,
  17: 7600,
  18: 8500,
  19: 9450,
  20: 10500,
};

/**
 * Get level based on total XP
 */
export function getLevel(xp: number): number {
  let level = 1;
  for (const [lvl, required] of Object.entries(LEVELS)) {
    if (xp >= required) {
      level = parseInt(lvl, 10);
    } else {
      break;
    }
  }
  return level;
}

/**
 * Get XP needed for next level.
 * Returns null when the current level is the max level.
 */
export function getXpForNextLevel(currentLevel: number): number | null {
  return LEVELS[currentLevel + 1] ?? null;
}

/**
 * Get progress percentage towards next level.
 * Returns 100 when at the max level.
 */
export function getLevelProgress(xp: number): number {
  const currentLevel = getLevel(xp);
  const currentLevelXp = LEVELS[currentLevel];
  const nextLevelXp = getXpForNextLevel(currentLevel);

  if (nextLevelXp === null) {
    return 100;
  }

  const xpInCurrentLevel = xp - currentLevelXp;
  const xpNeededForLevel = nextLevelXp - currentLevelXp;

  return Math.min(100, Math.round((xpInCurrentLevel / xpNeededForLevel) * 100));
}

export const LEVEL_TITLES: Record<number, string> = {
  1: 'Aprendiz',
  2: 'Explorador',
  3: 'Estudioso',
  4: 'Investigador',
  5: 'Erudito',
  6: 'Académico',
  7: 'Maestro',
  8: 'Sabio',
  9: 'Iluminado',
  10: 'Leyenda',
};

export function getLevelTitle(level: number): string {
  return LEVEL_TITLES[Math.min(level, 10)] ?? 'Leyenda';
}
